import { useState, useEffect, useCallback } from 'react';
import { Heart, Plus, ChevronDown, LogIn } from 'lucide-react';
import { showErrorToast } from '../../utils/toast';
import { MedicalCross } from './RoleSwitchIcon';

export type ProfileSwitchMenuUser = {
  id?: number | string;
  profiles?: Array<{
    profile_type: string;
    doctor_status?: string;
  }>;
  active_profile_type?: string;
};

function memberUnlockStorageKey(userId: number) {
  return `ma_member_switch_unlocked_v1_${userId}`;
}

type ProfileSwitchMenuProps = {
  user: ProfileSwitchMenuUser | null | undefined;
  profileSwitchBusy: boolean;
  setProfileSwitchBusy: (v: boolean) => void;
  setOpen: (v: boolean) => void;
  switchProfile: (t: 'doctor' | 'patient') => Promise<void>;
  onBecomeDoctor: () => Promise<void>;
  /** When true (e.g. onboarding nav lock), block all switches / add-role actions */
  disableInteractions?: boolean;
};

function RadioIndicator({ selected }: { selected: boolean }) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
        selected
          ? 'border-herbal-green bg-herbal-green shadow-sm'
          : 'border-gray-300 bg-white'
      }`}
      aria-hidden
    >
      {selected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
    </span>
  );
}

/** Healthcare provider — medical plus sign stands out clearly. */
function ProviderGlyph({ className = '' }: { className?: string }) {
  return (
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600 ring-1 ring-red-200/80 ${className}`}
      aria-hidden
    >
      <MedicalCross className="h-3.5 w-3.5" />
    </span>
  );
}

/** Patient — heart reads as personal wellness / member account. */
function PatientGlyph({ className = '' }: { className?: string }) {
  return (
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600 ring-1 ring-rose-200/80 ${className}`}
      aria-hidden
    >
      <Heart
        className="h-3.5 w-3.5"
        strokeWidth={2.25}
        fill="currentColor"
        fillOpacity={0.15}
      />
    </span>
  );
}

/**
 * Single profile / role panel for patient site and provider dashboard: radio-style
 * switch between Patient and Healthcare Provider, plus Add role for missing profiles.
 */
export function ProfileSwitchMenu({
  user,
  profileSwitchBusy,
  setProfileSwitchBusy,
  setOpen,
  switchProfile,
  onBecomeDoctor,
  disableInteractions = false,
}: ProfileSwitchMenuProps) {
  const profiles = user?.profiles ?? [];
  const hasDoctorProfile = profiles.some((p) => p.profile_type === 'doctor');
  const hasPatientProfile = profiles.some((p) => p.profile_type === 'patient');
  const doctorRow = profiles.find((p) => p.profile_type === 'doctor');
  const rawActive = user?.active_profile_type?.toLowerCase();
  const activeType: 'doctor' | 'patient' =
    rawActive === 'doctor' ? 'doctor' : 'patient';
  const doctorReviewRequested = doctorRow?.doctor_status === 'review_requested';

  const rowBase =
    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-herbal-green/35 focus-visible:ring-offset-1';

  /** Active role row — matches site herbal palette (replaces legacy sky-blue) */
  const selectedRow =
    'bg-leaf-mint/50 text-slate-900 ring-1 ring-herbal-green/25 shadow-sm';

  const userId =
    user?.id != null && !Number.isNaN(Number(user.id)) ? Number(user.id) : null;
  const [memberSwitchUnlocked, setMemberSwitchUnlocked] = useState(false);

  useEffect(() => {
    if (userId == null || typeof window === 'undefined') {
      setMemberSwitchUnlocked(false);
      return;
    }
    try {
      setMemberSwitchUnlocked(
        localStorage.getItem(memberUnlockStorageKey(userId)) === '1',
      );
    } catch {
      setMemberSwitchUnlocked(false);
    }
  }, [userId]);

  const persistMemberSwitchUnlock = useCallback(() => {
    setMemberSwitchUnlocked(true);
    if (userId == null || typeof window === 'undefined') return;
    try {
      localStorage.setItem(memberUnlockStorageKey(userId), '1');
    } catch {
      /* session-only unlock */
    }
  }, [userId]);

  const runSwitch = async (type: 'doctor' | 'patient') => {
    if (profileSwitchBusy) return;
    setProfileSwitchBusy(true);
    try {
      await switchProfile(type);
      if (type === 'patient' && hasPatientProfile && hasDoctorProfile) {
        persistMemberSwitchUnlock();
      }
      setOpen(false);
    } catch {
      showErrorToast(
        type === 'patient'
          ? 'Could not switch to patient profile.'
          : 'Could not switch to healthcare provider profile.',
      );
    } finally {
      setProfileSwitchBusy(false);
    }
  };

  const runBecomeDoctor = async () => {
    if (profileSwitchBusy) return;
    setProfileSwitchBusy(true);
    try {
      await onBecomeDoctor();
      setOpen(false);
    } catch (e) {
      showErrorToast(
        e instanceof Error
          ? e.message
          : 'Could not add healthcare provider profile.',
      );
    } finally {
      setProfileSwitchBusy(false);
    }
  };

  const showSwitchSection = hasPatientProfile || hasDoctorProfile;
  const [addRoleExpanded, setAddRoleExpanded] = useState(false);

  const hasAddableDoctorSlot = !hasDoctorProfile;
  const hasBothProfiles = hasPatientProfile && hasDoctorProfile;
  /** Before first visit to member site: only provider row; “Continue as a patient” stays under Add role. */
  const showProviderOnlyBeforeUnlock =
    hasBothProfiles && activeType === 'doctor' && !memberSwitchUnlocked;
  /** Healthcare provider first, Patient below — after unlock (or while on member site). */
  const showDualInSwitch =
    hasBothProfiles &&
    (activeType === 'patient' ||
      (activeType === 'doctor' && memberSwitchUnlocked));
  const showContinueAsUserInAddRole =
    hasBothProfiles &&
    activeType === 'doctor' &&
    hasPatientProfile &&
    !memberSwitchUnlocked;

  return (
    <div
      className={`px-1 pb-1 ${disableInteractions ? 'pointer-events-none select-none opacity-60' : ''}`}
      aria-hidden={disableInteractions ? true : undefined}
    >
      <div className="px-3 pb-1.5 pt-1 text-[11px] font-bold text-herbal-green-dark/90 tracking-widest uppercase">
        Profile
      </div>

      {doctorReviewRequested && activeType === 'patient' && (
        <div className="mx-2 mb-2 rounded-lg px-2.5 py-2 text-xs text-amber-900 bg-amber-50 border border-amber-100">
          Provider profile submitted for review. Switch to healthcare provider
          when you want to work in the provider area.
        </div>
      )}

      {showSwitchSection && (
        <>
          <div className="px-3 pb-1 pt-1 text-[10px] font-semibold text-herbal-green-dark/70 uppercase tracking-wide">
            Switch role
          </div>
          <div className="space-y-0.5">
            {showProviderOnlyBeforeUnlock && hasDoctorProfile && (
              <div
                className={`${rowBase} ${selectedRow} cursor-default`}
                aria-current="true"
              >
                <RadioIndicator selected />
                <ProviderGlyph />
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="font-medium">Healthcare provider</span>
                  <span className="text-[11px] font-normal text-gray-500">
                    Clinic &amp; remedies
                  </span>
                </span>
              </div>
            )}

            {showDualInSwitch && (
              <>
                {activeType === 'doctor' ? (
                  <>
                    <div
                      className={`${rowBase} ${selectedRow} cursor-default`}
                      aria-current="true"
                    >
                      <RadioIndicator selected />
                      <ProviderGlyph />
                      <span className="flex min-w-0 flex-col items-start leading-tight">
                        <span className="font-medium">Healthcare provider</span>
                        <span className="text-[11px] font-normal text-gray-500">
                          Clinic &amp; remedies
                        </span>
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={profileSwitchBusy}
                      onClick={() => void runSwitch('patient')}
                      className={`${rowBase} text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50`}
                    >
                      <RadioIndicator selected={false} />
                      <PatientGlyph />
                      <span className="flex min-w-0 flex-col items-start leading-tight">
                        <span className="font-medium">Patient</span>
                        <span className="text-[11px] font-normal text-gray-500">
                          Personal wellness
                        </span>
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className={`${rowBase} ${selectedRow} cursor-default`}
                      aria-current="true"
                    >
                      <RadioIndicator selected />
                      <PatientGlyph />
                      <span className="flex min-w-0 flex-col items-start leading-tight">
                        <span className="font-medium">Patient</span>
                        <span className="text-[11px] font-normal text-gray-500">
                          Personal wellness
                        </span>
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={profileSwitchBusy}
                      onClick={() => void runSwitch('doctor')}
                      className={`${rowBase} text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50`}
                    >
                      <RadioIndicator selected={false} />
                      <ProviderGlyph />
                      <span className="flex min-w-0 flex-col items-start leading-tight">
                        <span className="font-medium">Healthcare provider</span>
                        <span className="text-[11px] font-normal text-gray-500">
                          Clinic &amp; remedies
                        </span>
                      </span>
                    </button>
                  </>
                )}
              </>
            )}

            {!hasBothProfiles && hasPatientProfile && (
              <button
                type="button"
                disabled={profileSwitchBusy}
                onClick={() => {
                  if (activeType === 'patient') return;
                  void runSwitch('patient');
                }}
                className={`${rowBase} ${
                  activeType === 'patient'
                    ? `${selectedRow} cursor-default`
                    : 'text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50'
                }`}
              >
                <RadioIndicator selected={activeType === 'patient'} />
                <PatientGlyph />
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="font-medium">Patient</span>
                  <span className="text-[11px] font-normal text-gray-500">
                    Personal wellness
                  </span>
                </span>
              </button>
            )}

            {!hasBothProfiles && hasDoctorProfile && (
              <button
                type="button"
                disabled={profileSwitchBusy}
                onClick={() => {
                  if (activeType === 'doctor') return;
                  void runSwitch('doctor');
                }}
                className={`${rowBase} ${
                  activeType === 'doctor'
                    ? `${selectedRow} cursor-default`
                    : 'text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50'
                }`}
              >
                <RadioIndicator selected={activeType === 'doctor'} />
                <ProviderGlyph />
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="font-medium">Healthcare provider</span>
                  <span className="text-[11px] font-normal text-gray-500">
                    Clinic &amp; remedies
                  </span>
                </span>
              </button>
            )}
          </div>
        </>
      )}

      <div className="mx-2 my-2 border-t border-herbal-green/15" />

      <div className="px-3 pb-1 text-[10px] font-semibold text-herbal-green-dark/70 uppercase tracking-wide">
        Add role
      </div>
      <div className="px-2 pb-1">
        <button
          type="button"
          disabled={profileSwitchBusy}
          onClick={() => setAddRoleExpanded((v) => !v)}
          aria-expanded={addRoleExpanded}
          className={`${rowBase} w-full justify-between text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50`}
        >
          <span className="font-medium">
            {addRoleExpanded ? 'Hide role options' : 'See roles you can add'}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-herbal-green/80 transition-transform duration-200 ${addRoleExpanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {addRoleExpanded && (
        <div className="space-y-0.5 px-1">
          {showContinueAsUserInAddRole && (
            <>
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold text-herbal-green-dark/70 uppercase tracking-wide">
                Use your account as
              </p>
              <button
                type="button"
                disabled={profileSwitchBusy}
                onClick={() => void runSwitch('patient')}
                className={`${rowBase} border border-dashed border-herbal-green/30 bg-leaf-mint/25 text-slate-800 hover:bg-leaf-mint/45 hover:border-herbal-green/40 disabled:opacity-50`}
              >
                <PatientGlyph />
                <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
                  <span className="font-medium leading-tight">
                    Continue as a patient
                  </span>
                  <span className="text-[11px] font-normal text-gray-500 leading-snug">
                    Member site — remedies, family, wellness
                  </span>
                </span>
                <LogIn
                  className="h-4 w-4 shrink-0 text-herbal-green"
                  aria-hidden
                />
              </button>
            </>
          )}

          {hasAddableDoctorSlot && (
            <button
              type="button"
              disabled={profileSwitchBusy}
              onClick={() => void runBecomeDoctor()}
              className={`${rowBase} text-slate-700 hover:bg-herbal-green/8 active:bg-herbal-green/12 disabled:opacity-50`}
            >
              <RadioIndicator selected={false} />
              <ProviderGlyph />
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span className="font-medium">Healthcare provider</span>
                <span className="text-[11px] font-normal text-gray-500">
                  Add clinic &amp; remedy tools
                </span>
              </span>
              <Plus
                className="h-4 w-4 shrink-0 text-herbal-green"
                aria-hidden
              />
            </button>
          )}

          <div
            className={`${rowBase} cursor-not-allowed text-gray-400 opacity-70`}
            title="Coming soon"
            aria-disabled
          >
            <RadioIndicator selected={false} />
            <span className="w-7 shrink-0" />
            <span className="font-medium">Professional</span>
          </div>
          <div
            className={`${rowBase} cursor-not-allowed text-gray-400 opacity-70`}
            title="Coming soon"
            aria-disabled
          >
            <RadioIndicator selected={false} />
            <span className="w-7 shrink-0" />
            <span className="font-medium">Support staff</span>
          </div>
        </div>
      )}
    </div>
  );
}
